import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, Play, Image as ImageIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import type { CompanionMessage } from "../../lib/companionGuide";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";

interface CompanionBannerProps {
  message: CompanionMessage;
  onDismiss: (id: string) => void;
  compact?: boolean;
  /** Minimal inline bubble — embeddable inside other components (e.g. hero sections) */
  mini?: boolean;
  className?: string;
}

const CompanionBanner: React.FC<CompanionBannerProps> = ({
  message,
  onDismiss,
  compact = false,
  mini = false,
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [showMedia, setShowMedia] = useState(false);

  const hasMedia = !!message.video || !!message.image;

  // Auto-hide if configured
  useEffect(() => {
    if (message.autoHideSeconds) {
      timerRef.current = setTimeout(() => {
        onDismiss(message.id);
      }, message.autoHideSeconds * 1000);
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [message.id, message.autoHideSeconds, onDismiss]);

  // ── Mini variant — single-line full-width bar ───────────────────────────
  if (mini) {
    return (
      <>
        <div
          className={cn(
            "relative flex items-center gap-2 w-full",
            "bg-ai/[0.04] border border-ai/10 rounded-lg",
            "px-3 py-1.5 text-start",
            "animate-companion-enter",
            className
          )}
        >
          {/* Emoji */}
          <span className="text-sm flex-shrink-0 select-none">
            {message.emoji}
          </span>

          {/* Title + message inline */}
          <p className="flex-1 min-w-0 text-xs text-gray-600 truncate">
            {message.title && (
              <span className="font-semibold text-gray-800 me-1">{message.title}</span>
            )}
            {message.message}
          </p>

          {/* Inline actions */}
          {message.action && (
            <button
              onClick={() => navigate(message.action!.route)}
              className="flex-shrink-0 text-[11px] font-medium text-ai/70 hover:text-ai transition-colors whitespace-nowrap"
            >
              {message.action.label}
            </button>
          )}
          {hasMedia && (
            <button
              onClick={() => setShowMedia(true)}
              className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-ai/70 hover:text-ai transition-colors whitespace-nowrap"
            >
              {message.video ? (
                <Play className="w-3 h-3" />
              ) : (
                <ImageIcon className="w-3 h-3" />
              )}
              {message.video
                ? t("companion_watch_guide") || "Watch"
                : t("companion_view_guide") || "View"}
            </button>
          )}

          {/* Dismiss */}
          {message.dismissible && (
            <button
              onClick={() => onDismiss(message.id)}
              className="flex-shrink-0 p-0.5 rounded text-gray-300 hover:text-gray-500 transition-colors"
              aria-label={t("dismiss") || "Dismiss"}
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Media lightbox dialog */}
        {hasMedia && (
          <Dialog open={showMedia} onOpenChange={setShowMedia}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden">
              {message.video ? (
                <div className="relative w-full aspect-video">
                  {isEmbedUrl(message.video) ? (
                    <iframe
                      src={toEmbedUrl(message.video)}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={message.title || "Guide video"}
                    />
                  ) : (
                    <video
                      src={message.video}
                      controls
                      autoPlay
                      className="w-full h-full object-contain bg-black"
                    />
                  )}
                </div>
              ) : message.image ? (
                <div className="flex items-center justify-center p-4">
                  <img
                    src={message.image}
                    alt={message.title || "Guide image"}
                    className="max-w-full max-h-[80vh] object-contain rounded-lg"
                  />
                </div>
              ) : null}
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  // ── Standard variant ──────────────────────────────────────────────────────
  return (
    <>
      <div
        className={cn(
          "relative animate-companion-enter bg-gradient-to-r from-ai/5 via-white to-transparent border border-ai/10 rounded-2xl",
          compact ? "p-3" : "p-5",
          message.celebration && "animate-shine bg-[length:400%_100%] bg-gradient-to-r from-ai/5 via-ai/10 to-ai/5",
          className
        )}
      >
        {/* Dismiss button */}
        {message.dismissible && (
          <button
            onClick={() => onDismiss(message.id)}
            className="absolute top-3 end-3 text-gray-300 hover:text-gray-500 transition-colors z-10"
            aria-label={t("dismiss") || "Dismiss"}
          >
            <X className="w-4 h-4" />
          </button>
        )}

        <div className="flex gap-3 items-start">
          {/* Emoji + Icon */}
          <div
            className={cn(
              "flex-shrink-0 rounded-xl flex items-center justify-center bg-gradient-to-br from-ai/10 to-ai/5",
              compact ? "w-9 h-9" : "w-11 h-11"
            )}
          >
            <span className={cn("select-none", compact ? "text-lg" : "text-xl")}>
              {message.emoji}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 pe-6">
            {message.title && (
              <h3
                className={cn(
                  "font-semibold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent",
                  compact ? "text-sm" : "text-base"
                )}
              >
                {message.title}
              </h3>
            )}
            <p
              className={cn(
                "text-gray-600 leading-relaxed",
                compact ? "text-xs mt-0.5" : "text-sm mt-1"
              )}
            >
              {message.message}
            </p>

            {/* Action buttons row */}
            {(message.action || hasMedia) && (
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {/* Navigation action */}
                {message.action && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-ai/20 text-ai hover:bg-ai/5"
                    onClick={() => navigate(message.action!.route)}
                  >
                    {message.action.label}
                  </Button>
                )}

                {/* Video / Image guide CTA */}
                {hasMedia && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-ai/70 hover:text-ai hover:bg-ai/5 gap-1.5"
                    onClick={() => setShowMedia(true)}
                  >
                    {message.video ? (
                      <Play className="w-3.5 h-3.5" />
                    ) : (
                      <ImageIcon className="w-3.5 h-3.5" />
                    )}
                    {message.video
                      ? t("companion_watch_guide") || "Watch guide"
                      : t("companion_view_guide") || "View guide"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Media lightbox dialog */}
      {hasMedia && (
        <Dialog open={showMedia} onOpenChange={setShowMedia}>
          <DialogContent className="max-w-3xl p-0 overflow-hidden">
            {message.video ? (
              <div className="relative w-full aspect-video">
                {isEmbedUrl(message.video) ? (
                  <iframe
                    src={toEmbedUrl(message.video)}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={message.title || "Guide video"}
                  />
                ) : (
                  <video
                    src={message.video}
                    controls
                    autoPlay
                    className="w-full h-full object-contain bg-black"
                  />
                )}
              </div>
            ) : message.image ? (
              <div className="flex items-center justify-center p-4">
                <img
                  src={message.image}
                  alt={message.title || "Guide image"}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg"
                />
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isEmbedUrl(url: string): boolean {
  return (
    url.includes("youtube.com") ||
    url.includes("youtu.be") ||
    url.includes("vimeo.com") ||
    url.includes("loom.com")
  );
}

function toEmbedUrl(url: string): string {
  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/
  );
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

  // Loom
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;

  return url;
}

export default CompanionBanner;
